import { useState } from 'react';
import { Card, Button, Input, Textarea, Label } from './../ui';
import { Plus, Pencil, Trash2, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { ExtracurricularEntry } from '@scholarship-app/types';

interface Props {
  activities: ExtracurricularEntry[];
  editing: boolean;
  onChange: (next: ExtracurricularEntry[]) => void;
}

const empty = (): ExtracurricularEntry => ({
  name: '',
  role: '',
  organization: '',
  start_date: '',
  end_date: null,
  hours_per_week: 0,
  description: '',
});

export function ProfileActivitiesEditor({ activities, editing, onChange }: Props) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<ExtracurricularEntry | null>(null);

  function startAdd() {
    setAdding(true);
    setExpandedIndex(null);
    setDraft(empty());
  }
  function startEdit(i: number) {
    setAdding(false);
    setExpandedIndex(i);
    setDraft({ ...activities[i] });
  }
  function cancel() {
    setAdding(false);
    setExpandedIndex(null);
    setDraft(null);
  }
  function save() {
    if (!draft) return;
    if (adding) {
      onChange([...activities, draft]);
    } else if (expandedIndex !== null && draft) {
      const next = [...activities];
      next[expandedIndex] = draft;
      onChange(next);
    }
    cancel();
  }
  function remove(i: number) {
    if (!confirm(`Delete activity: "${activities[i].name}"?`)) return;
    onChange(activities.filter((_, idx) => idx !== i));
  }
  function toggleExpand(i: number) {
    if (expandedIndex === i) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(i);
      setDraft(null);
    }
  }

  return (
    <div className="space-y-2">
      {activities.length === 0 && !adding && (
        <p className="text-sm text-soft">No activities yet.</p>
      )}
      {activities.map((a, i) => {
        const isExpanded = expandedIndex === i;
        const isEditingThis = isExpanded && draft;
        return (
          <Card key={i} className="overflow-hidden">
            <button
              type="button"
              className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-cream text-left"
              onClick={() => toggleExpand(i)}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink">{a.name || '(unnamed activity)'}</div>
                <div className="text-xs text-soft">
                  {a.role}{a.role && a.organization ? ' · ' : ''}{a.organization}
                  {a.hours_per_week ? ` · ${a.hours_per_week} hrs/wk` : ''}
                </div>
              </div>
              {isExpanded ? <ChevronUp size={16} className="text-soft" /> : <ChevronDown size={16} className="text-soft" />}
            </button>
            {isExpanded && (
              <div className="px-3 py-3 border-t border-line">
                {isEditingThis ? (
                  <ActivityEditForm draft={draft as ExtracurricularEntry} setDraft={setDraft} onSave={save} onCancel={cancel} />
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                      <div><strong>Role:</strong> {a.role || '—'}</div>
                      <div><strong>Organization:</strong> {a.organization || '—'}</div>
                      <div><strong>Start:</strong> {a.start_date || '—'}</div>
                      <div><strong>End:</strong> {a.end_date || 'ongoing'}</div>
                      <div><strong>Hours/week:</strong> {a.hours_per_week ?? 0}</div>
                    </div>
                    {a.description && <p className="text-soft mt-2">{a.description}</p>}
                    {editing && (
                      <div className="flex justify-end gap-2 mt-2">
                        <Button type="button" size="sm" variant="ghost" onClick={() => startEdit(i)}>
                          <Pencil size={12} className="mr-1" />Edit
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)}>
                          <Trash2 size={12} className="mr-1" />Delete
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
      {adding && draft && (
        <ActivityEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
      )}
      {editing && !adding && (
        <Button type="button" size="sm" variant="secondary" onClick={startAdd}>
          <Plus size={12} className="mr-1" />Add activity
        </Button>
      )}
    </div>
  );
}

function ActivityEditForm({ draft, setDraft, onSave, onCancel }: { draft: ExtracurricularEntry; setDraft: (d: ExtracurricularEntry) => void; onSave: () => void; onCancel: () => void }) {
  return (
    <Card className="p-3 bg-forest-50/40 border-forest-200 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label>Activity name</Label>
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Charlotte Youth Orchestra" />
        </div>
        <div>
          <Label>Role</Label>
          <Input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="e.g. Violin" />
        </div>
        <div className="md:col-span-2">
          <Label>Organization</Label>
          <Input value={draft.organization} onChange={(e) => setDraft({ ...draft, organization: e.target.value })} />
        </div>
        <div>
          <Label>Start date</Label>
          <Input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
        </div>
        <div>
          <Label>End date (optional)</Label>
          <Input type="date" value={draft.end_date ?? ''} onChange={(e) => setDraft({ ...draft, end_date: e.target.value || null })} />
        </div>
        <div>
          <Label>Hours per week</Label>
          <Input type="number" min="0" max="168" value={draft.hours_per_week} onChange={(e) => setDraft({ ...draft, hours_per_week: Number(e.target.value) })} />
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}><X size={12} className="mr-1" />Cancel</Button>
        <Button type="button" size="sm" onClick={onSave}><Check size={12} className="mr-1" />Save</Button>
      </div>
    </Card>
  );
}
